<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import * as d3 from 'd3';
  import { prepareChartData } from '../lib/utils';
  import type { DataPoint, ForecastPoint } from '../lib/types';
  
  export let actualData: DataPoint[] = [];
  export let forecastData: ForecastPoint[] = [];
  export let showConfidenceIntervals: boolean = true;
  export let height: number = 400;
  export let animated: boolean = true;
  export let title: string = '';
  
  // Not using dispatch currently, but might need it for event handling in the future
  // const dispatch = createEventDispatcher();
  
  let chartContainer: HTMLDivElement;
  let tooltip: any; // Using any to avoid typing issues with d3 selections
  let svg: any; // Using any to avoid typing issues with d3 selections
  let chartData = prepareChartData(actualData, forecastData);
  let width: number;
  let previousData = JSON.stringify({ actual: actualData, forecast: forecastData });
  
  // Функция для создания/обновления графика
  function createChart() {
    if (!chartContainer) return;

    // Очистка существующего графика
    d3.select(chartContainer).select('svg').remove();
    
    // Получение ширины контейнера
    width = chartContainer.clientWidth;
    
    // Создание нового SVG
    svg = d3.select(chartContainer)
      .append('svg')
      .attr('width', width)
      .attr('height', height);
    
    // Создание всплывающей подсказки
    tooltip = d3.select(chartContainer)
      .append('div')
      .attr('class', 'chart-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('border', '1px solid #ddd')
      .style('border-radius', '4px')
      .style('padding', '8px')
      .style('pointer-events', 'none')
      .style('box-shadow', '0 4px 8px rgba(0,0,0,0.1)')
      .style('z-index', '10');
    
    // Update the chart
    updateChart();
  }
  
  // Функция для обновления графика с новыми данными
  function updateChart() {
    if (!svg || !chartContainer) return;
    
    // Пересчет данных графика
    chartData = prepareChartData(actualData, forecastData);
    if (chartData.data.length === 0) return;
    
    // Расчет отступов
    const margin = { top: 30, right: 50, bottom: 40, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    // Создание шкал
    const xScale = d3.scaleTime()
      .domain(chartData.xDomain)
      .range([0, innerWidth]);
    
    const yScale = d3.scaleLinear()
      .domain(chartData.yDomain)
      .range([innerHeight, 0]);
    
    // Создание генераторов линий
    const actualLine = d3.line()
      .x((d: any) => xScale(d.timestamp))
      .y((d: any) => yScale(d.value))
      .curve(d3.curveMonotoneX);
    
    const forecastLine = d3.line()
      .x((d: any) => xScale(d.timestamp))
      .y((d: any) => yScale(d.value))
      .curve(d3.curveMonotoneX);
    
    // Создание группы графика
    const chart = svg.selectAll('.chart-group')
      .data([null])
      .join('g')
      .attr('class', 'chart-group')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Добавление заголовка, если он предоставлен
    if (title) {
      chart.selectAll('.chart-title')
        .data([null])
        .join('text')
        .attr('class', 'chart-title')
        .attr('x', innerWidth / 2)
        .attr('y', -10)
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .text(title);
    }
    
    // Add gridlines
    chart.selectAll('.grid-y')
      .data([null])
      .join('g')
      .attr('class', 'grid-y')
      .call(d3.axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat(() => '')
      )
      .selectAll('line')
      .attr('stroke', '#e0e0e0')
      .attr('stroke-opacity', 0.7);
    
    chart.selectAll('.grid-x')
      .data([null])
      .join('g')
      .attr('class', 'grid-x')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-innerHeight)
        .tickFormat(() => '')
      )
      .selectAll('line')
      .attr('stroke', '#e0e0e0')
      .attr('stroke-opacity', 0.7);
    
    // Add X and Y axes
    chart.selectAll('.x-axis')
      .data([null])
      .join('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale));
    
    chart.selectAll('.y-axis')
      .data([null])
      .join('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale));
    
    // X axis label
    chart.selectAll('.x-axis-label')
      .data([null])
      .join('text')
      .attr('class', 'x-axis-label')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 35)
      .attr('text-anchor', 'middle')
      .text('Time');
    
    // Y axis label
    chart.selectAll('.y-axis-label')
      .data([null])
      .join('text')
      .attr('class', 'y-axis-label')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .text('Value');
    
    // Filter data by type
    const actualPoints = chartData.data.filter(d => d.type === 'actual');
    const forecastPoints = chartData.data.filter(d => d.type === 'forecast');
    
    // Add confidence intervals for forecast
    if (showConfidenceIntervals && forecastPoints.length > 0) {
      const confidenceArea = d3.area()
        .x((d: any) => xScale(d.timestamp))
        .y0((d: any) => d.lower !== undefined ? yScale(d.lower) : yScale(d.value))
        .y1((d: any) => d.upper !== undefined ? yScale(d.upper) : yScale(d.value))
        .curve(d3.curveMonotoneX);
      
      chart.selectAll('.confidence-area')
        .data([forecastPoints.filter(d => d.lower !== undefined && d.upper !== undefined)])
        .join('path')
        .attr('class', 'confidence-area')
        .attr('fill', 'rgba(67, 97, 238, 0.2)')
        .attr('d', confidenceArea);
    }
    
    // Add actual data line
    if (actualPoints.length > 0) {
      const actualPath = chart.selectAll('.actual-line')
        .data([actualPoints])
        .join('path')
        .attr('class', 'actual-line')
        .attr('fill', 'none')
        .attr('stroke', '#4361ee')
        .attr('stroke-width', 2)
        .attr('d', actualLine);
      
      // Add animation if enabled
      if (animated) {
        const pathLength = actualPath.node()?.getTotalLength() || 0;
        actualPath
          .attr('stroke-dasharray', pathLength)
          .attr('stroke-dashoffset', pathLength)
          .transition()
          .duration(1000)
          .attr('stroke-dashoffset', 0);
      }
      
      // Add data points for actual data
      chart.selectAll('.actual-point')
        .data(actualPoints)
        .join('circle')
        .attr('class', 'actual-point')
        .attr('cx', d => xScale(d.timestamp))
        .attr('cy', d => yScale(d.value))
        .attr('r', 4)
        .attr('fill', '#4361ee')
        .on('mouseover', function(this: SVGCircleElement, event: MouseEvent, d: d3.ChartPoint) {
          d3.select(this).attr('r', 6);
          tooltip.transition().duration(200).style('opacity', 0.9);
          tooltip.html(`
            <div style="font-weight: bold;">Actual Data</div>
            <div>Date: ${d.timestamp.toLocaleString()}</div>
            <div>Value: ${d.value.toFixed(2)}</div>
          `)
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', function(this: SVGCircleElement) {
          d3.select(this).attr('r', 4);
          tooltip.transition().duration(500).style('opacity', 0);
        });
    }
    
    // Add forecast data line
    if (forecastPoints.length > 0) {
      const forecastPath = chart.selectAll('.forecast-line')
        .data([forecastPoints])
        .join('path')
        .attr('class', 'forecast-line')
        .attr('fill', 'none')
        .attr('stroke', '#f72585')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5')
        .attr('d', forecastLine);
      
      // Add animation if enabled
      if (animated) {
        const pathLength = forecastPath.node()?.getTotalLength() || 0;
        forecastPath
          .attr('stroke-dasharray', pathLength)
          .attr('stroke-dashoffset', pathLength)
          .transition()
          .duration(1000)
          .attr('stroke-dashoffset', 0);
      }
      
      // Add data points for forecast data
      chart.selectAll('.forecast-point')
        .data(forecastPoints)
        .join('circle')
        .attr('class', 'forecast-point')
        .attr('cx', d => xScale(d.timestamp))
        .attr('cy', d => yScale(d.value))
        .attr('r', 4)
        .attr('fill', '#f72585')
        .on('mouseover', function(this: SVGCircleElement, event: MouseEvent, d: d3.ChartPoint) {
          d3.select(this).attr('r', 6);
          
          let tooltipContent = `
            <div style="font-weight: bold;">Forecast Data</div>
            <div>Date: ${d.timestamp.toLocaleString()}</div>
            <div>Value: ${d.value.toFixed(2)}</div>
          `;
          
          if (d.lower !== undefined && d.upper !== undefined) {
            tooltipContent += `
              <div>Lower: ${d.lower.toFixed(2)}</div>
              <div>Upper: ${d.upper.toFixed(2)}</div>
            `;
          }
          
          tooltip.transition().duration(200).style('opacity', 0.9);
          tooltip.html(tooltipContent)
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 28) + 'px');
        })
        .on('mouseout', function(this: SVGCircleElement) {
          d3.select(this).attr('r', 4);
          tooltip.transition().duration(500).style('opacity', 0);
        });
    }
    
    // Add legend
    const legendData = [
      { label: 'Actual Data', color: '#4361ee', type: 'line' },
      { label: 'Forecast', color: '#f72585', type: 'dashed' }
    ];
    
    if (showConfidenceIntervals && forecastPoints.some(p => p.lower !== undefined && p.upper !== undefined)) {
      legendData.push({ label: 'Confidence Interval', color: 'rgba(67, 97, 238, 0.2)', type: 'area' });
    }
    
    const legend = chart.selectAll('.legend')
      .data([null])
      .join('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${innerWidth - 180}, 10)`);
    
    const legendItems = legend.selectAll('.legend-item')
      .data(legendData)
      .join('g')
      .attr('class', 'legend-item')
      .attr('transform', (_, i) => `translate(0, ${i * 20})`);
    
    // Legend symbols
    legendItems.each(function(this: SVGGElement, d) {
      const item = d3.select(this);
      interface LegendItem { 
        type: string; 
        color: string; 
        label: string;
      }
      const legendItem = d as LegendItem;
      
      if (legendItem.type === 'line') {
        item.append('line')
          .attr('x1', 0)
          .attr('y1', 7)
          .attr('x2', 20)
          .attr('y2', 7)
          .attr('stroke', legendItem.color)
          .attr('stroke-width', 2);
      } else if (legendItem.type === 'dashed') {
        item.append('line')
          .attr('x1', 0)
          .attr('y1', 7)
          .attr('x2', 20)
          .attr('y2', 7)
          .attr('stroke', legendItem.color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '3,3');
      } else if (legendItem.type === 'area') {
        item.append('rect')
          .attr('x', 0)
          .attr('y', 2)
          .attr('width', 20)
          .attr('height', 10)
          .attr('fill', legendItem.color);
      }
    });
    
    legendItems.append('text')
      .attr('x', 25)
      .attr('y', 10)
      .text(d => d.label)
      .attr('font-size', '12px');
  }
  
  onMount(() => {
    createChart();
    
    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      createChart();
    });
    
    resizeObserver.observe(chartContainer);
    
    return () => {
      resizeObserver.disconnect();
    };
  });
  
  afterUpdate(() => {
    // Only update if data has changed
    const currentData = JSON.stringify({ actual: actualData, forecast: forecastData });
    if (currentData !== previousData) {
      previousData = currentData;
      if (svg) {
        updateChart();
      }
    }
  });
</script>

<div class="time-series-chart" bind:this={chartContainer}>
  {#if !actualData.length && !forecastData.length}
    <div class="no-data-message">
      <p>No data available to display</p>
    </div>
  {/if}
</div>

<style>
  .time-series-chart {
    width: 100%;
    height: 100%;
    position: relative;
  }
  
  .no-data-message {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #6c757d;
  }
  
  :global(.time-series-chart .x-axis path),
  :global(.time-series-chart .y-axis path) {
    stroke: #adb5bd;
  }
  
  :global(.time-series-chart .x-axis text),
  :global(.time-series-chart .y-axis text) {
    fill: #495057;
    font-size: 12px;
  }
  
  :global(.time-series-chart .grid-y line),
  :global(.time-series-chart .grid-x line) {
    stroke: #e9ecef;
  }
  
  :global(.time-series-chart .x-axis-label),
  :global(.time-series-chart .y-axis-label) {
    fill: #495057;
    font-size: 12px;
  }
  
  :global(.time-series-chart .chart-title) {
    fill: #212529;
    font-size: 16px;
  }
</style>
